import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import {
  Wallet, ArrowDownToLine, ArrowUpFromLine, Clock, Hourglass, TrendingUp, Bot, Users, Gift, Lock, Unlock, ShieldCheck,
} from "lucide-react";
import { useFetch, PageHeader, StatCard, Panel, Loading, StatusBadge, Empty } from "@/components/common";
import { usd, fmtDate, TXN_LABELS } from "@/lib/api";

export const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-[#242F42] bg-[#121722] px-3 py-2 text-xs">
      <p className="text-slate-400">{label}</p>
      <p className="num text-[#10B981]">{usd(payload[0].value)}</p>
    </div>
  ) : null;

export const EarnChart = ({ data, testid }) =>
  data?.length ? (
    <div className="h-64 px-2 py-4" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ left: 0, right: 12, top: 8 }}>
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="date" tickLine={false} axisLine={false} tickFormatter={(d) => d.slice(5)} />
          <YAxis tickLine={false} axisLine={false} width={50} />
          <Tooltip content={<ChartTip />} />
          <Area type="monotone" dataKey="amount" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  ) : (
    <div className="flex h-64 flex-col items-center justify-center gap-2 text-sm text-slate-500" data-testid={testid}>
      <TrendingUp size={22} /> No data yet. Activate an AI plan to start earning daily returns.
    </div>
  );

function BonusCard({ b }) {
  if (!b?.credited) return null;
  const unlocked = b.status === "unlocked";
  return (
    <div className="nx-card relative overflow-hidden p-6 border-[#10B981]/30" data-testid="welcome-bonus-card">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#10B981]/10 blur-2xl" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#10B981]/15 text-[#10B981]"><Gift size={20} /></span>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-display text-lg font-semibold text-white">Welcome Bonus</p>
              <StatusBadge status={b.status} testid="welcome-bonus-status" />
            </div>
            <p className="num mt-0.5 text-2xl font-semibold text-[#10B981]" data-testid="welcome-bonus-amount">{usd(b.amount)} USD</p>
            <p className="mt-2 max-w-2xl text-xs text-slate-400 leading-relaxed" data-testid="welcome-bonus-terms">{b.terms}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-300">
          {unlocked ? <Unlock size={14} className="text-[#10B981]" /> : <Lock size={14} className="text-amber-400" />}
          {unlocked ? "Now part of your withdrawable balance" : (
            <Link to="/deposit" className="nx-btn-primary" data-testid="bonus-unlock-deposit-btn">Make first deposit to unlock</Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: d, loading } = useFetch("/dashboard");
  if (loading || !d) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Dashboard" title={`Hello, ${d.user.name.split(" ")[0]}`}
        subtitle={<>User ID <span className="num text-[#10B981]" data-testid="dashboard-user-id">{d.user.user_code}</span></>}
        actions={<>
          <Link to="/deposit" className="nx-btn-primary" data-testid="quick-deposit-btn"><ArrowDownToLine size={16} />Deposit</Link>
          <Link to="/withdraw" className="nx-btn-ghost" data-testid="quick-withdraw-btn"><ArrowUpFromLine size={16} />Withdraw</Link>
        </>} />

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="nx-card p-6 lg:col-span-5" data-testid="balance-hero">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Account balance</p>
          <p className="num mt-3 text-4xl font-semibold text-white" data-testid="user-total-balance">{usd(d.total_balance)}</p>
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#1E2738] pt-5">
            <div>
              <p className="text-xs text-slate-500">Withdrawable</p>
              <p className="num mt-1 text-lg text-[#10B981]" data-testid="user-withdrawable-balance">{usd(d.withdrawable_balance)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Locked bonus</p>
              <p className="num mt-1 text-lg text-amber-400" data-testid="user-locked-bonus">{usd(d.user.bonus_balance)}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:col-span-7">
          <StatCard label="Accumulated returns" value={usd(d.accumulated_returns)} icon={TrendingUp} accent testid="stat-accumulated-returns" />
          <StatCard label="Active in AI plans" value={usd(d.active_investment)} icon={Bot} testid="stat-active-investment" />
          <StatCard label="Referral earnings" value={usd(d.referral_earnings)} icon={Users} testid="stat-referral-earnings" />
          <StatCard label="Total deposits" value={usd(d.total_deposits)} icon={Wallet} testid="stat-total-deposits" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Total withdrawals" value={usd(d.total_withdrawals)} icon={ArrowUpFromLine} testid="stat-total-withdrawals" />
        <StatCard label="Pending deposits" value={usd(d.pending_deposits)} icon={Clock} testid="stat-pending-deposits" />
        <StatCard label="Pending withdrawals" value={usd(d.pending_withdrawals)} icon={Hourglass} testid="stat-pending-withdrawals" />
      </div>

      <BonusCard b={d.welcome_bonus} />

      <div className="grid gap-6 lg:grid-cols-12">
        <Panel title="Daily returns (last 30 days)" className="lg:col-span-7"><EarnChart data={d.returns_chart} testid="returns-chart" /></Panel>
        <Panel title="Recent activity" className="lg:col-span-5" action={<Link to="/transactions" className="text-xs text-[#10B981]" data-testid="view-all-txns">View all</Link>}>
          <div className="divide-y divide-[#1E2738]" data-testid="recent-transactions">
            {d.recent_transactions.length === 0 && <Empty text="No transactions yet" />}
            {d.recent_transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-white">{t.description}</p>
                  <p className="text-xs text-slate-500">{TXN_LABELS[t.type] || t.type} · {fmtDate(t.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className={`num text-sm ${t.direction === "debit" ? "text-slate-300" : "text-[#10B981]"}`}>{t.direction === "debit" ? "−" : "+"}{usd(t.amount)}</p>
                  <StatusBadge status={t.status} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <p className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck size={13} /> Trading involves risk. Returns are credited by plan rules and are not guaranteed.</p>
    </div>
  );
}
